// src/components/FriendsTab.jsx
// Friends — people search + following/followers list + profile navigation
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54",
  primaryDim: "#595149",
  primaryContainer: "#ede0d5",
  bg: "#faf9f6",
  surfaceLow: "#f4f4f0",
  container: "#edeeea",
  surfaceLowest: "#ffffff",
  onSurface: "#2f3430",
  onSurfaceVariant: "#5c605c",
  outlineVariant: "#8a8e8a",
  error: "#9e422c",
};

export default function FriendsTab({ onViewUserProfile, onRequestCountChange }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [subTab, setSubTab] = useState("following"); // "following" | "followers"
  const [followingList, setFollowingList] = useState([]);
  const [followerList, setFollowerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pendingSent, setPendingSent] = useState(new Set());
  const [processingId, setProcessingId] = useState(null);

  // People search state
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const [searchError, setSearchError] = useState("");

  const fetchAll = useCallback(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE}/follows/me/following`),
      axios.get(`${API_BASE}/follows/me/followers`),
      axios.get(`${API_BASE}/follows/me/requests`),
      axios.get(`${API_BASE}/follows/me/pending`),
    ]).then(([fg, fr, rq, pend]) => {
      setFollowingList(fg.data);
      setFollowerList(fr.data);
      setRequests(rq.data);
      setPendingSent(new Set(pend.data.map((u) => u.id || u.user_id)));
      if (onRequestCountChange) onRequestCountChange(rq.data.length);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user, onRequestCountChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // People search
  const handleSearch = async (q = query) => {
    if (!q.trim() || !user) return;
    setSearchError(""); setUserResults([]);
    setSearching(true);
    try {
      const res = await axios.get(`${API_BASE}/users/search?q=${encodeURIComponent(q.trim())}`);
      const filtered = Array.isArray(res.data) ? res.data.filter((u) => u.id !== user?.user_id) : [];
      if (filtered.length > 0) setUserResults(filtered);
      else setSearchError(t("search.no_users"));
    } catch {
      setSearchError(t("search.search_failed"));
    } finally {
      setSearching(false);
    }
  };

  const handleSearchFollow = async (targetId) => {
    if (!user) return;
    setActionLoading(targetId);
    try {
      await axios.post(`${API_BASE}/follows/${targetId}`);
      // Refresh following list
      const fg = await axios.get(`${API_BASE}/follows/me/following`);
      setFollowingList(fg.data);
    } catch {} finally { setActionLoading(null); }
  };

  const handleUnfollow = async (targetId) => {
    setActionLoading(targetId);
    try {
      await axios.delete(`${API_BASE}/follows/${targetId}`);
      setFollowingList((prev) => prev.filter((u) => (u.id || u.user_id) !== targetId));
    } catch { alert(t("follow.unfollow_failed")); }
    finally { setActionLoading(null); }
  };

  const handleCancelRequest = async (targetId) => {
    setActionLoading(targetId);
    try {
      await axios.delete(`${API_BASE}/follows/${targetId}`);
      setPendingSent((prev) => { const next = new Set(prev); next.delete(targetId); return next; });
    } catch { alert(t("follow.request_failed")); }
    finally { setActionLoading(null); }
  };

  const handleFollowBack = async (targetId) => {
    setActionLoading(targetId);
    try {
      const res = await axios.post(`${API_BASE}/follows/${targetId}`);
      if (res.data?.status === "pending") {
        setPendingSent((prev) => new Set([...prev, targetId]));
      } else {
        const fg = await axios.get(`${API_BASE}/follows/me/following`);
        setFollowingList(fg.data);
      }
    } catch { alert(t("follow.follow_failed")); }
    finally { setActionLoading(null); }
  };

  const handleAccept = async (fromId) => {
    setProcessingId(fromId);
    try {
      await axios.post(`${API_BASE}/follows/requests/${fromId}/accept`);
      setRequests((prev) => prev.filter((r) => r.from_user_id !== fromId));
      if (onRequestCountChange) onRequestCountChange(requests.length - 1);
      fetchAll();
    } catch { alert(t("follow.accept_failed")); }
    finally { setProcessingId(null); }
  };

  const handleReject = async (fromId) => {
    setProcessingId(fromId);
    try {
      await axios.post(`${API_BASE}/follows/requests/${fromId}/reject`);
      setRequests((prev) => prev.filter((r) => r.from_user_id !== fromId));
      if (onRequestCountChange) onRequestCountChange(requests.length - 1);
    } catch { alert(t("follow.decline_failed")); }
    finally { setProcessingId(null); }
  };

  // Check which followers I'm not following back
  const followingIds = new Set(followingList.map((u) => u.id || u.user_id));
  const isFollowing = (id) => followingIds.has(id);

  const UserRow = ({ u, showAction }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 0",
      borderBottom: `1px solid ${C.container}`,
    }}>
      <div
        onClick={() => onViewUserProfile?.(u.nickname)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          flex: 1, minWidth: 0, cursor: "pointer",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: u.profile_photo_url
            ? `url(${u.profile_photo_url}) center/cover`
            : `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: FH, fontStyle: "italic", fontSize: 16, color: "#fff6ef", fontWeight: 700,
        }}>
          {!u.profile_photo_url && u.nickname?.[0]?.toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontFamily: FL, fontSize: 14, fontWeight: 600,
            color: C.onSurface, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {u.nickname}
          </p>
          {u.bio && (
            <p style={{
              margin: "2px 0 0", fontFamily: FL, fontSize: 11, color: C.outlineVariant,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {u.bio}
            </p>
          )}
        </div>
      </div>
      {showAction}
    </div>
  );

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: "100%", padding: "48px 16px", textAlign: "center" }}>
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 13, color: "#a8a29e" }}>{t("auth.loading")}</p>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100%" }}>
      {/* People search bar */}
      <div style={{ padding: "14px 16px 0" }}>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <div style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            pointerEvents: "none",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, color: searching ? C.primary : "#a8a29e", transition: "color 0.2s" }}>person_search</span>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder={t("search.people_hint")}
            style={{
              width: "100%", padding: "10px 12px 10px 32px",
              background: C.surfaceLow, border: "none",
              borderRadius: 8, outline: "none",
              fontFamily: FL, color: C.onSurface,
              boxSizing: "border-box", transition: "background 0.2s",
            }}
            ref={(el) => { if (el) el.style.setProperty("font-size", "14px", "important"); }}
            onFocus={(e) => e.target.style.background = C.container}
            onBlur={(e) => e.target.style.background = C.surfaceLow}
          />
          {query.trim() && (
            <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
              {searching ? (
                <span style={{ fontFamily: FL, fontSize: 12, color: "#a8a29e" }}>{t("search.searching")}</span>
              ) : (
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: C.primary, cursor: "pointer" }}
                  onClick={() => handleSearch()}>arrow_forward</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search results */}
      {(userResults.length > 0 || searchError) && (
        <div style={{ padding: "0 16px 8px" }}>
          {searchError && (
            <div style={{
              padding: "10px 14px", background: "#fef0ec",
              borderLeft: "3px solid #9e422c",
              borderRadius: 6, marginBottom: 8,
              fontFamily: FH, fontStyle: "italic", fontSize: 14, color: "#9e422c",
            }}>
              {searchError}
            </div>
          )}
          {userResults.length > 0 && (
            <section style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
                <span style={{
                  fontFamily: FL, fontSize: 12, fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.2em",
                  color: C.primary,
                }}>
                  {t("search.people_found")}
                </span>
                <button onClick={() => { setUserResults([]); setQuery(""); setSearchError(""); }} style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: FL, fontSize: 12, color: C.outlineVariant,
                }}>
                  {t("myplace.clear")}
                </button>
              </div>
              {userResults.map((u) => {
                const uid = u.id;
                const alreadyFollowing = isFollowing(uid);
                return (
                  <div key={uid} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 12px", borderRadius: 10,
                    background: C.surfaceLowest, transition: "background 0.2s",
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                    onMouseLeave={(e) => e.currentTarget.style.background = C.surfaceLowest}
                  >
                    <div
                      onClick={() => onViewUserProfile?.(u.nickname)}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flex: 1 }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                        background: u.profile_photo_url
                          ? `url(${u.profile_photo_url}) center/cover`
                          : `linear-gradient(135deg, #595149, #655d54)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: FH, fontStyle: "italic",
                        fontSize: 13, color: "#fff6ef", fontWeight: 700,
                      }}>
                        {!u.profile_photo_url && u.nickname?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontFamily: FL, fontSize: 16, fontWeight: 600, color: C.onSurface }}>
                          {u.nickname}
                        </p>
                        {u.instagram_url && (
                          <p style={{ margin: "2px 0 0", fontFamily: FL, fontSize: 14, color: "#E1306C" }}>
                            Instagram
                          </p>
                        )}
                      </div>
                    </div>
                    {alreadyFollowing ? (
                      <button
                        onClick={() => handleUnfollow(uid)}
                        disabled={actionLoading === uid}
                        style={{
                          padding: "6px 14px",
                          border: "1px solid rgba(101,93,84,0.15)",
                          borderRadius: 8, background: "none",
                          fontFamily: FL, fontSize: 14, fontWeight: 600,
                          color: C.outlineVariant, cursor: "pointer",
                          opacity: actionLoading === uid ? 0.5 : 1,
                        }}
                      >
                        {t("social.following")}
                      </button>
                    ) : pendingSent.has(uid) ? (
                      <button
                        onClick={() => handleCancelRequest(uid)}
                        disabled={actionLoading === uid}
                        style={{
                          padding: "6px 14px",
                          border: "1px solid rgba(101,93,84,0.15)",
                          borderRadius: 8, background: "none",
                          fontFamily: FL, fontSize: 14, fontWeight: 600,
                          color: C.outlineVariant, cursor: "pointer",
                          opacity: actionLoading === uid ? 0.5 : 1,
                        }}
                      >
                        {t("follow.requested")}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSearchFollow(uid)}
                        disabled={actionLoading === uid}
                        style={{
                          padding: "6px 14px", border: "none", borderRadius: 8,
                          background: C.primary, color: "#fff6ef",
                          fontFamily: FL, fontSize: 14, fontWeight: 700,
                          cursor: "pointer",
                          opacity: actionLoading === uid ? 0.5 : 1,
                        }}
                      >
                        {t("social.follow")}
                      </button>
                    )}
                  </div>
                );
              })}
            </section>
          )}
        </div>
      )}

      {/* Segment toggle: Following / Followers */}
      <div style={{ padding: "0 16px" }}>
        <div style={{
          display: "flex", gap: 0, width: "100%",
          background: "rgba(237,238,234,0.6)",
          borderRadius: 10, padding: 3,
        }}>
          {[
            { key: "following", label: `${t("profile.following")} ${followingList.length}` },
            { key: "followers", label: `${t("profile.followers")} ${followerList.length}` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSubTab(tab.key)}
              style={{
                flex: 1, padding: "8px 0",
                background: subTab === tab.key ? "#ffffff" : "transparent",
                border: "none", borderRadius: 8,
                fontFamily: FL, fontSize: 13, fontWeight: subTab === tab.key ? 700 : 500,
                color: subTab === tab.key ? C.primary : C.outlineVariant,
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: subTab === tab.key ? "0 1px 4px rgba(47,52,48,0.08)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: "8px 16px 16px" }}>
        {/* Follow requests (always on top) */}
        {requests.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={{
              margin: "8px 0 6px", fontFamily: FL, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.1em", color: C.primary,
            }}>
              {t("profile.follow_requests")} {requests.length}
            </p>
            {requests.map((req) => (
              <div key={req.from_user_id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 0", borderBottom: `1px solid ${C.container}`,
              }}>
                <div
                  onClick={() => onViewUserProfile?.(req.from_nickname)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    flex: 1, minWidth: 0, cursor: "pointer",
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                    background: `linear-gradient(135deg, ${C.primaryDim}, ${C.primary})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: FH, fontStyle: "italic", fontSize: 16, color: "#fff6ef", fontWeight: 700,
                  }}>
                    {req.from_nickname?.[0]?.toUpperCase()}
                  </div>
                  <p style={{
                    margin: 0, fontFamily: FL, fontSize: 14, fontWeight: 600,
                    color: C.onSurface, flex: 1,
                  }}>
                    {req.from_nickname}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => handleAccept(req.from_user_id)}
                    disabled={processingId === req.from_user_id}
                    style={{
                      padding: "6px 14px", borderRadius: 6,
                      background: C.primary, border: "none",
                      fontFamily: FL, fontSize: 11, fontWeight: 700,
                      color: "#fff6ef", cursor: "pointer",
                    }}
                  >
                    {t("notification.accept")}
                  </button>
                  <button
                    onClick={() => handleReject(req.from_user_id)}
                    disabled={processingId === req.from_user_id}
                    style={{
                      padding: "6px 14px", borderRadius: 6,
                      background: "none", border: `1px solid ${C.container}`,
                      fontFamily: FL, fontSize: 11, fontWeight: 600,
                      color: C.outlineVariant, cursor: "pointer",
                    }}
                  >
                    {t("notification.decline")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Following list */}
        {subTab === "following" && (
          <>
            {followingList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 16px" }}>
                <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 15, color: "#a8a29e", margin: "0 0 6px" }}>
                  {t("follow.no_following")}
                </p>
                <p style={{ fontFamily: FL, fontSize: 11, color: "#a8a29e" }}>
                  {t("follow.find_people")}
                </p>
              </div>
            ) : (
              followingList.map((u) => (
                <UserRow key={u.id || u.user_id} u={u} showAction={
                  <button
                    onClick={() => handleUnfollow(u.id || u.user_id)}
                    disabled={actionLoading === (u.id || u.user_id)}
                    style={{
                      padding: "6px 12px", borderRadius: 6, flexShrink: 0,
                      background: "none", border: `1px solid rgba(158,66,44,0.15)`,
                      fontFamily: FL, fontSize: 11, fontWeight: 600,
                      color: C.error, cursor: "pointer",
                      opacity: actionLoading === (u.id || u.user_id) ? 0.5 : 1,
                    }}
                  >
                    {t("follow.unfollow")}
                  </button>
                } />
              ))
            )}
          </>
        )}

        {/* Followers list */}
        {subTab === "followers" && (
          <>
            {followerList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 16px" }}>
                <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 15, color: "#a8a29e", margin: "0 0 6px" }}>
                  {t("follow.no_followers")}
                </p>
              </div>
            ) : (
              followerList.map((u) => {
                const uid = u.id || u.user_id;
                const alreadyFollowing = isFollowing(uid);
                return (
                  <UserRow key={uid} u={u} showAction={
                    uid !== user?.user_id && pendingSent.has(uid) ? (
                      <button
                        onClick={() => handleCancelRequest(uid)}
                        disabled={actionLoading === uid}
                        style={{
                          padding: "6px 12px", borderRadius: 6, flexShrink: 0,
                          background: "none", border: `1px solid ${C.container}`,
                          fontFamily: FL, fontSize: 11, fontWeight: 600,
                          color: C.outlineVariant, cursor: "pointer",
                          opacity: actionLoading === uid ? 0.5 : 1,
                        }}
                      >
                        {t("follow.requested")}
                      </button>
                    ) : uid !== user?.user_id && !alreadyFollowing ? (
                      <button
                        onClick={() => handleFollowBack(uid)}
                        disabled={actionLoading === uid}
                        style={{
                          padding: "6px 14px", borderRadius: 6, flexShrink: 0,
                          background: C.primary, border: "none",
                          fontFamily: FL, fontSize: 11, fontWeight: 700,
                          color: "#fff6ef", cursor: "pointer",
                          opacity: actionLoading === uid ? 0.5 : 1,
                        }}
                      >
                        {t("follow.follow_back")}
                      </button>
                    ) : alreadyFollowing ? (
                      <span style={{
                        padding: "6px 12px", borderRadius: 6,
                        border: `1px solid ${C.container}`,
                        fontFamily: FL, fontSize: 11, fontWeight: 600,
                        color: C.outlineVariant, flexShrink: 0,
                      }}>
                        {t("profile.following")}
                      </span>
                    ) : null
                  } />
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}
